import React, { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import HabboImage from "../HabboImage";
import { ImageLightbox } from "../FranklyCloudModal";
import { fetchProfileColor, getAvatarBgStyle } from "../../services/profileColorService";
import {
    listPostComments,
    createPostComment,
    hideProfileComment,
    createProfileEngagement,
    deleteProfileEngagement,
} from "../../services/api";
import {
    useEmoteContext,
    renderEmotesInHtmlMemo,
    stripEmoteSentinels,
    EmotePickerModal,
    PlainEmoteInput,
} from "../../services/emoteService";
import { toast } from "react-toastify";

// ── Constants ──────────────────────────────────────────────────────────────────

const COMMENT_MAX_CHARS = 300;
const REPLY_PAGE_SIZE = 5;
const REPLY_CACHE_TTL_MS = 2 * 60 * 1000;
const REPLY_FETCH_CONCURRENCY = 4;

// ── Utilities ──────────────────────────────────────────────────────────────────

const encodeEmojis = (s) => String(s ?? '').replace(/[\u{80}-\u{10FFFF}]/gu, (ch) => `&#${ch.codePointAt(0)};`);
const decodeEntities = (s) => String(s ?? '').replace(/&#(\d+);/g, (_, n) => String.fromCodePoint(Number(n)));

const isSafeExternalUrl = (url) => {
    try {
        const u = new URL(url);
        return u.protocol === "http:" || u.protocol === "https:";
    } catch {
        return false;
    }
};

const toTitleCase = (s) => s.replace(/\b\w/g, (c) => c.toUpperCase());

const getUrlPageName = (url) => {
    try {
        const u = new URL(url);
        const parts = u.pathname.split("/").filter(Boolean);
        const last = parts[parts.length - 1] || u.hostname;
        const name = toTitleCase(decodeURIComponent(last).replace(/[-_+]/g, " ").replace(/\.[^.]+$/, "").trim());
        const base = name || toTitleCase(u.hostname.slice(0, 20));
        if (base.length <= 20) return base;
        const cut = base.slice(0, 20).replace(/\s+\S*$/, "");
        return (cut || base.slice(0, 20)) + "…";
    } catch {
        return url.slice(0, 20);
    }
};

const URL_REGEX = /\b((https?:\/\/|www\.)[^\s<]+[^\s<\.)])/gi;
const MENTION_REGEX = /(^|[\s(])@([A-Za-z0-9_]{2,32})\b/g;

const externalizeAndMentionHtml = (rawHtml, { mentionHrefForName } = {}) => {
    const input = String(rawHtml ?? "");
    if (!input) return "";

    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${input}</div>`, "text/html");
    const root = doc.body.firstChild;

    const unwrapAnchors = (node) => {
        if (!node) return;
        const anchors = node.querySelectorAll ? node.querySelectorAll("a") : [];
        anchors.forEach((a) => {
            const text = doc.createTextNode(a.textContent || "");
            a.replaceWith(text);
        });
    };

    unwrapAnchors(root);

    root.querySelectorAll('img').forEach(img => {
        if (img.classList.contains('fc-image')) {
            const src = img.getAttribute('src') || '';
            if (src.startsWith('data:image/') || src.startsWith('/uploads/UserUploads/')) {
                const safe = doc.createElement('img');
                safe.className = 'fc-image block max-w-full h-auto my-1';
                safe.src = src;
                safe.alt = img.getAttribute('alt') || '';
                img.replaceWith(safe);
            } else {
                img.remove();
            }
        } else {
            img.remove();
        }
    });

    const replaceInTextNode = (textNode) => {
        const text = textNode.nodeValue || "";
        if (!text) return;

        const parts = [];
        let lastIndex = 0;

        const pushText = (s) => {
            if (!s) return;
            parts.push(doc.createTextNode(s));
        };

        const pushExternal = (url) => {
            const safeUrl = String(url || "");
            const link = doc.createElement("a");
            link.textContent = getUrlPageName(safeUrl);
            link.setAttribute("href", "#");
            link.setAttribute("data-external-url", safeUrl);
            link.setAttribute("data-external", "1");
            link.className = "text-blue-700 hover:underline font-bold";
            parts.push(link);
        };

        const pushMention = (fullPrefix, name) => {
            const href = typeof mentionHrefForName === "function" ? mentionHrefForName(name) : null;
            if (!href) {
                pushText(`${fullPrefix}@${name}`);
                return;
            }
            pushText(fullPrefix);
            const a = doc.createElement("a");
            a.textContent = `@${name}`;
            a.setAttribute("href", href);
            a.className = "text-blue-700 hover:underline font-bold";
            a.setAttribute("data-mention", "1");
            parts.push(a);
        };

        const matches = [];
        let m;

        URL_REGEX.lastIndex = 0;
        while ((m = URL_REGEX.exec(text)) !== null) {
            matches.push({ type: "url", index: m.index, len: m[0].length, value: m[0] });
        }

        MENTION_REGEX.lastIndex = 0;
        while ((m = MENTION_REGEX.exec(text)) !== null) {
            const prefix = m[1] || "";
            const name = m[2] || "";
            const idx = m.index;
            const full = `${prefix}@${name}`;
            matches.push({ type: "mention", index: idx, len: full.length, prefix, name });
        }

        if (matches.length === 0) return;

        matches.sort((a, b) => a.index - b.index);

        const filtered = [];
        let cursor = -1;
        for (const item of matches) {
            if (item.index < cursor) continue;
            filtered.push(item);
            cursor = item.index + item.len;
        }

        for (const item of filtered) {
            pushText(text.slice(lastIndex, item.index));
            if (item.type === "url") {
                let url = item.value;
                if (/^www\./i.test(url)) url = `https://${url}`;
                if (isSafeExternalUrl(url)) pushExternal(url);
                else pushText(item.value);
            } else if (item.type === "mention") {
                pushMention(item.prefix, item.name);
            }
            lastIndex = item.index + item.len;
        }

        pushText(text.slice(lastIndex));

        const frag = doc.createDocumentFragment();
        parts.forEach((n) => frag.appendChild(n));
        textNode.replaceWith(frag);
    };

    const walk = (node) => {
        if (!node) return;
        if (node.nodeType === Node.TEXT_NODE) {
            replaceInTextNode(node);
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const children = Array.from(node.childNodes);
        children.forEach(walk);
    };

    walk(root);

    return root.innerHTML || "";
};

const normalizeEngagement = (e) => ({
    ...e,
    fromUserId: e?.fromUserId ?? e?.fromUserID ?? e?.from_user_id ?? e?.userId ?? e?.userID ?? e?.user_id ?? null,
    targetId: e?.targetId ?? e?.targetID ?? e?.target_id ?? null,
    targetType: e?.targetType ?? e?.target_type ?? null,
    engagementType: e?.engagementType ?? e?.engagement_type ?? null,
});

const blockConfirmBody = (user) => (
    <div style={{ whiteSpace: "normal" }}>
        <div>Blocking a user on Frankly does the following:</div>
        <ul style={{ margin: "8px 0 8px 18px" }}>
            <li>Removes all interactions with <strong>{user}</strong> from your page.</li>
            <li>Stops <strong>{user}</strong> seeing your profile.</li>
            <li>Stops you interacting on <strong>{user}</strong>&apos;s page.</li>
        </ul>
        <div style={{ marginTop: 8 }}>
            Note: While blocking is reversible by visiting that user&apos;s page, the removal of all interactions (comments, etc.) is permanent and cannot be reversed.
        </div>
        <div style={{ marginTop: 12 }}>
            Are you sure you want to block <strong>{user}</strong>?
        </div>
    </div>
);

const getLikesCount = (obj) => {
    const v = obj?.likesCount ?? obj?.likes_count ?? obj?.likescount ?? obj?.LikesCount ?? obj?.likes ?? obj?.Likes ?? 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const normalizeComment = (c) => {
    if (!c) return c;

    const visibility =
        c.visibility ??
        c.Visibility ??
        c.visibilityLabel ??
        c.VisibilityLabel ??
        c.visibility_label ??
        null;

    const directParent =
        c.parentCommentID ??
        c.parentCommentId ??
        c.parent_comment_id ??
        c.parentID ??
        c.parentId ??
        null;

    let parentCommentID = directParent != null && directParent !== "" ? Number(directParent) : null;
    if (!Number.isFinite(parentCommentID)) parentCommentID = null;

    if (parentCommentID == null) {
        const vis = String(visibility ?? "");
        const replyMatch = vis.match(/^reply:(\d+)$/);
        parentCommentID = replyMatch ? Number(replyMatch[1]) : null;
    }

    return {
        ...c,
        id: c.id ?? c.ID,
        likesCount: getLikesCount(c),
        parentCommentID,
        visibility,
    };
};

// ── Reply cache (exported so the parent can flush it on refresh) ──────────────

export const replyCache = {};
export const replyInflight = {};
let replyFetchActive = 0;
const replyFetchWaiters = [];

const withReplySemaphore = (fn) =>
    new Promise((resolve, reject) => {
        const run = () => {
            replyFetchActive++;
            Promise.resolve()
                .then(fn)
                .then(resolve, reject)
                .finally(() => {
                    replyFetchActive--;
                    const next = replyFetchWaiters.shift();
                    if (next) next();
                });
        };
        if (replyFetchActive < REPLY_FETCH_CONCURRENCY) run();
        else replyFetchWaiters.push(run);
    });

const replyCacheKey = (postId, parentIdNum) => `${String(postId)}:${String(parentIdNum)}`;

const readReplyCache = (key) => {
    const entry = replyCache[key];
    if (!entry) return null;
    if (Date.now() - entry.ts > REPLY_CACHE_TTL_MS) return null;
    return Array.isArray(entry.replies) ? entry.replies : null;
};

const writeReplyCache = (key, repliesArr) => {
    replyCache[key] = { ts: Date.now(), replies: Array.isArray(repliesArr) ? repliesArr : [] };
};

const fetchRepliesForParent = async (postId, parentIdNum, opts = {}) => {
    const pid = Number(parentIdNum);
    if (!postId || !Number.isFinite(pid) || pid <= 0) return [];

    const key = replyCacheKey(postId, pid);
    const force = Boolean(opts?.force);

    if (!force) {
        const cached = readReplyCache(key);
        if (cached) return cached;
        if (replyInflight[key]) return replyInflight[key];
    }

    replyInflight[key] = withReplySemaphore(async () => {
        const visibilityLabel = `reply:${pid}`;
        const res = await listPostComments(postId, 20, 0, visibilityLabel);
        const raw = Array.isArray(res?.comments) ? res.comments : [];
        const normalized = raw.map(normalizeComment);
        const filtered = normalized.filter((c) => c?.parentCommentID != null && Number(c.parentCommentID) === pid);
        const sorted = [...filtered].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        writeReplyCache(key, sorted);
        return sorted;
    });

    try {
        return await replyInflight[key];
    } finally {
        delete replyInflight[key];
    }
};

// ── MentionSuggestions ─────────────────────────────────────────────────────────

const MentionSuggestions = ({ open, items, anchorRect, onPick, onClose }) => {
    if (!open || !anchorRect || !items || items.length === 0) return null;

    const style = {
        position: "fixed",
        top: Math.min(window.innerHeight - 12, anchorRect.bottom + 6),
        left: Math.min(window.innerWidth - 12, anchorRect.left),
        zIndex: 999,
        width: 260,
        maxHeight: 220,
        overflowY: "auto",
    };

    return (
        <div style={style} className="border border-black/40 bg-offwhite rounded shadow-md overflow-hidden">
            <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-coal/60 border-b border-black/10 bg-black/5">
                Tag a friend
            </div>
            {items.map((f) => (
                <button
                    key={f.userID || f.habboName}
                    type="button"
                    onClick={() => onPick(f)}
                    className="w-full text-left px-2 py-2 hover:bg-black/5 flex items-center gap-2"
                >
                    <span className="font-bold text-sm text-coal">@{f.habboName}</span>
                </button>
            ))}
            <button type="button" onClick={onClose} className="w-full px-2 py-2 text-[11px] text-coal/60 hover:bg-black/5 border-t border-black/10">
                Close
            </button>
        </div>
    );
};

// ── ReplyItem ──────────────────────────────────────────────────────────────────

const ReplyItem = ({ reply, replyHabboName, canDelete, onDelete, timeAgo, commentBg, onExternalLink }) => {
    const navigate = useNavigate();
    const emoteCtx = useEmoteContext();
    const [avatarBg, setAvatarBg] = React.useState(reply.profileBg || '#5A2929');

    const replyHtmlObj = React.useMemo(() => {
        const raw = reply?.content ?? reply?.Content ?? reply?.text ?? reply?.Text ?? "";
        const decoded = stripEmoteSentinels(decodeEntities(raw));
        const mentionHrefForName = (name) => `/members/profile/${encodeURIComponent(String(name || "").trim())}`;
        const html = renderEmotesInHtmlMemo(externalizeAndMentionHtml(decoded, { mentionHrefForName }), emoteCtx);
        return { __html: html };
    }, [reply, emoteCtx]);

    React.useEffect(() => {
        if (!reply.profileBg) {
            fetchProfileColor(replyHabboName).then(setAvatarBg);
        }
    }, [replyHabboName, reply.profileBg]);

    const goProfile = () => { if (!replyHabboName || replyHabboName === 'Unknown') return; navigate(`/members/profile/${encodeURIComponent(replyHabboName)}`); };

    return (
        <div className="flex gap-2 px-2 py-1.5 border-b border-black/5 last:border-b-0" style={{ backgroundColor: commentBg }}>
            <div
                className="w-[30px] h-[30px] relative rounded border border-black/15 overflow-hidden shrink-0 cursor-pointer hover:brightness-95"
                style={getAvatarBgStyle(avatarBg)}
                onClick={goProfile}
            >
                <HabboImage habbo={replyHabboName} hotel="COM" size="a" direction={2} head_direction={2} gesture="sml" headonly className="absolute top-5 left-1/2 -translate-x-1/2 -translate-y-1/2 max-w-none" />
            </div>
            <div className="flex-1 min-w-0 flex flex-col">
                <div className="flex items-center justify-between gap-1">
                    <span onClick={goProfile} className="font-bold text-xs text-coal cursor-pointer hover:underline leading-none">{replyHabboName}</span>
                    <div className="flex items-center gap-1.5 shrink-0">
                        <span className="text-[10px] text-coal/40">{timeAgo(reply.createdAt)}</span>
                        {canDelete && (
                            <button onClick={onDelete} className="text-coal/30 hover:text-red-500" title="Delete reply">
                                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12"/></svg>
                            </button>
                        )}
                    </div>
                </div>
                <div
                    className="text-xs text-coal/85 mt-0.5 break-words leading-snug"
                    onClick={(e) => {
                        const a = e.target?.closest?.("a[data-external='1']");
                        if (!a) return;
                        e.preventDefault();
                        const url = a.getAttribute("data-external-url") || "";
                        if (typeof onExternalLink === "function") onExternalLink(url);
                    }}
                    dangerouslySetInnerHTML={replyHtmlObj}
                />
            </div>
        </div>
    );
};

// ── ReplyThread ────────────────────────────────────────────────────────────────

const ReplyThread = ({
                         parentCommentId,
                         postId,
                         profileUsername,
                         currentUserId,
                         currentUserHabboName,
                         isPageOwner,
                         isAdmin,
                         requestConfirm,
                         commentBg,
                         onExternalLink,
                         onOpenEmotePicker,
                         timeAgo,
                         replyCount: initialReplyCount = 0,
                         replies: initialReplies = [],
                         onReplyPosted,
                         restrictCommenting,
                         restrictionExpiry,
                         isGuest,
                         postRef,
                     }) => {
    const parentIdNum = Number(parentCommentId);
    const visibilityLabel = `reply:${parentIdNum}`;
    const syncKey = `${String(postId)}:${String(parentIdNum)}`;

    const emoteCtx = useEmoteContext() ?? { packs: [], emotesByPack: {} };

    const [open, setOpen] = React.useState(false);
    const handleToggle = () => setOpen(v => !v);
    const [replyText, setReplyText] = React.useState("");
    const [justPosted, setJustPosted] = React.useState(null);
    const [olderOffset, setOlderOffset] = React.useState(0);

    const [threadReplies, setThreadReplies] = React.useState(() => (Array.isArray(initialReplies) ? initialReplies : []));
    const [loading, setLoading] = React.useState(false);
    const [loaded, setLoaded] = React.useState(Array.isArray(initialReplies) && initialReplies.length > 0);

    const [knownCount, setKnownCount] = React.useState(() => {
        const n = Number(initialReplyCount);
        if (Number.isFinite(n) && n > 0) return n;
        return Array.isArray(initialReplies) ? initialReplies.length : 0;
    });

    const matchesOptimisticReply = (srv, opt) => {
        const srvName = String(srv?.habboName || srv?.HabboName || "").trim().toLowerCase();
        const optName = String(opt?.habboName || opt?.HabboName || "").trim().toLowerCase();
        const srvContent = String(srv?.content || "");
        const optContent = String(opt?.content || "");
        if (srvContent && optContent && srvContent !== optContent) return false;
        if (srvName && optName && srvName !== optName) return false;

        const srvT = new Date(srv?.createdAt).getTime();
        const optT = new Date(opt?.createdAt).getTime();
        if (Number.isFinite(srvT) && Number.isFinite(optT) && Math.abs(srvT - optT) > 2 * 60 * 1000) return false;

        return true;
    };

    React.useEffect(() => {
        const next = Array.isArray(initialReplies) ? initialReplies : [];
        if (next.length > 0) {
            setThreadReplies(next);
            setLoaded(true);
            setKnownCount((c) => Math.max(c, next.length));
            return;
        }
        const n = Number(initialReplyCount);
        if (Number.isFinite(n) && n > 0) setKnownCount((c) => Math.max(c, n));
    }, [initialReplies, initialReplyCount, syncKey]);

    const fetchReplies = React.useCallback(
        async ({ silent, force } = {}) => {
            const isSilent = Boolean(silent);
            if (!postId || !Number.isFinite(parentIdNum) || parentIdNum <= 0) return;

            if (!isSilent) setLoading(true);
            try {
                const repliesArr = await fetchRepliesForParent(postId, parentIdNum, { force });
                setThreadReplies(Array.isArray(repliesArr) ? repliesArr : []);
                setKnownCount((c) => Math.max(Number(c) || 0, Array.isArray(repliesArr) ? repliesArr.length : 0));
                setLoaded(true);

                setJustPosted((prev) => {
                    if (!prev) return prev;
                    const found = Array.isArray(repliesArr) && repliesArr.some((r) => matchesOptimisticReply(r, prev));
                    return found ? null : prev;
                });
            } catch {
                if (!isSilent) toast.error("Failed to load replies.");
            } finally {
                if (!isSilent) setLoading(false);
            }
        },
        [postId, parentIdNum]
    );

    const prefetchedKeyRef = React.useRef(null);
    React.useEffect(() => {
        if (!postId || !Number.isFinite(parentIdNum) || parentIdNum <= 0) return;
        if (prefetchedKeyRef.current === syncKey) return;
        prefetchedKeyRef.current = syncKey;
        fetchReplies({ silent: true });
    }, [syncKey, postId, parentIdNum, fetchReplies]);

    React.useEffect(() => {
        if (!open) return;
        if (loaded || loading) return;
        fetchReplies({ silent: false });
    }, [open, loaded, loading, fetchReplies]);


    const handlePost = async (textOverride) => {
        const text = stripEmoteSentinels(String(textOverride ?? replyText ?? "").replace(/\r/g, "")).trim();
        if (!text) return;

        if (restrictCommenting) {
            const expiry = restrictionExpiry ? ` until ${new Date(restrictionExpiry).toLocaleString()}` : '';

            toast.warning(`You are restricted from commenting${expiry}.`);
            return;
        }

        if (text.length > COMMENT_MAX_CHARS) {
            toast.warning(`Reply too long. Max ${COMMENT_MAX_CHARS} characters.`);
            return;
        }
        if (!currentUserHabboName) return;
        if (!postId || !Number.isFinite(parentIdNum) || parentIdNum <= 0) return;

        const now = new Date().toISOString();
        const encoded = encodeEmojis(text);

        const optimistic = normalizeComment({
            id: `optimistic-reply-${Date.now()}`,
            content: encoded,
            createdAt: now,
            likesCount: 0,
            Engagements: [],
            habboName: currentUserHabboName,
            visibility: visibilityLabel,
            isOptimistic: true,
        });

        setJustPosted(optimistic);

        if (typeof onReplyPosted === "function") {
            onReplyPosted({
                kind: "optimistic-reply",
                postId,
                parentCommentId: parentIdNum,
                reply: optimistic,
            });
        }

        setKnownCount((c) => (Number.isFinite(Number(c)) ? Number(c) : 0) + 1);
        setReplyText("");

        let createdOk = false;
        try {
            const res = await createPostComment(postId, encoded, visibilityLabel);
            if (res?.success === false) throw new Error("create failed");
            createdOk = true;
        } catch {
            createdOk = false;
        }

        if (!createdOk) {
            setJustPosted(null);
            setKnownCount((c) => Math.max(0, (Number.isFinite(Number(c)) ? Number(c) : 0) - 1));
            toast.error("Failed to post reply. Please try again.");
            return;
        }

        setOpen(true);

        try {
            await fetchReplies({ silent: true, force: true });
        } catch {}

        if (typeof onReplyPosted === "function") await onReplyPosted();
    };

    React.useEffect(() => { if (postRef) postRef.current = handlePost; });

    const baseCount = Array.isArray(threadReplies) ? threadReplies.length : 0;
    const countForLabel =
        Math.max(Number.isFinite(Number(knownCount)) ? Number(knownCount) : 0, baseCount) + (justPosted ? 1 : 0);

    const windowStart = Math.max(0, baseCount - REPLY_PAGE_SIZE - olderOffset);
    const windowEnd = baseCount - olderOffset;

    const windowedReplies = (threadReplies || []).slice(windowStart, windowEnd);
    const hasPrevious = windowStart > 0;

    const displayedReplies = [
        ...windowedReplies,
        ...(justPosted ? [justPosted] : []),
    ];

    return (
        <div className="ml-12 -mt-1 pb-1">
            <button
                onClick={handleToggle}
                className="flex items-center gap-1.5 text-[10px] font-bold text-coal/40 hover:text-coal/70 transition-colors py-0.5"
            >
                <svg
                    width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round"
                    className={`transition-transform duration-150 ${open ? "rotate-90" : ""}`}
                >
                    <polyline points="9 18 15 12 9 6" />
                </svg>
                {open ? `Hide Replies (${countForLabel || 0})` : `See Replies... (${countForLabel || 0})`}
            </button>

            {open && (
                <div className="border-l-2 border-black/10 ml-1 mt-1">
                    {loading && (
                        <div className="px-3 py-2 text-[10px] text-coal/40">Loading replies...</div>
                    )}

                    {loaded && hasPrevious && (
                        <button
                            onClick={() => setOlderOffset((o) => o + REPLY_PAGE_SIZE)}
                            className="w-full px-3 py-1.5 text-[10px] font-bold text-coal/50 hover:bg-black/5 text-left uppercase border-b border-black/5"
                        >
                            ↑ Show previous {Math.min(REPLY_PAGE_SIZE, windowStart)}
                        </button>
                    )}

                    {loaded && displayedReplies.map((reply) => {
                        const replyHabboName = reply.habboName || reply.HabboName || reply.habbo_name || reply.habbo ||
                            reply.authorName || reply.username || reply.userName || 'Unknown';
                        const replyId = reply.id || reply.ID;
                        const isOwnReply = String(replyHabboName || '').trim().toLowerCase() === String(currentUserHabboName || '').trim().toLowerCase();
                        const canDelete = Boolean(currentUserHabboName) && (isOwnReply || isPageOwner || Boolean(isAdmin));

                        const handleDeleteReply = () => requestConfirm({
                            title: 'Delete reply?',
                            message: 'Delete this reply?',
                            onConfirm: async () => {
                                try {
                                    await hideProfileComment(replyId);
                                    setThreadReplies((prev) => (Array.isArray(prev) ? prev.filter(r => String(r.id || r.ID) !== String(replyId)) : []));
                                    setKnownCount((c) => Math.max(0, c - 1));

                                    const key = replyCacheKey(postId, parentIdNum);
                                    const cached = readReplyCache(key);
                                    if (cached) writeReplyCache(key, cached.filter(r => String(r.id || r.ID) !== String(replyId)));

                                    if (justPosted?.id === replyId) setJustPosted(null);
                                    if (typeof onReplyPosted === "function") await onReplyPosted();
                                } catch {
                                    toast.error("Failed to delete reply.");
                                }
                            }
                        });

                        return (
                            <ReplyItem
                                key={replyId}
                                reply={reply}
                                replyHabboName={replyHabboName}
                                canDelete={canDelete}
                                onDelete={handleDeleteReply}
                                timeAgo={timeAgo}
                                commentBg={commentBg}
                                onExternalLink={onExternalLink}
                                onOpenEmotePicker={onOpenEmotePicker}
                            />
                        );
                    })}

                    {loaded && olderOffset > 0 && windowEnd < baseCount && (
                        <button
                            onClick={() => setOlderOffset((o) => Math.max(0, o - REPLY_PAGE_SIZE))}
                            className="w-full px-3 py-1.5 text-[10px] font-bold text-coal/50 hover:bg-black/5 text-left uppercase border-t border-black/5"
                        >
                            ↓ Show next {Math.min(REPLY_PAGE_SIZE, baseCount - windowEnd)}
                        </button>
                    )}

                    {currentUserHabboName && (
                        <div className="flex items-center gap-2 border-t border-black/5 bg-white/30 px-3 py-2">
                            <PlainEmoteInput
                                value={replyText}
                                onChange={setReplyText}
                                placeholder={isGuest ? "Login to comment." : restrictCommenting ? "You are restricted from commenting." : "Write a reply..."}
                                emoteCtx={emoteCtx}
                                maxLength={COMMENT_MAX_CHARS + 20}
                                disabled={restrictCommenting}
                                className="text-[11px]"
                                onSubmit={restrictCommenting ? undefined : handlePost}
                                onOpenEmotePicker={typeof onOpenEmotePicker === "function" ? onOpenEmotePicker : undefined}
                            />
                            <button
                                onClick={restrictCommenting ? undefined : () => handlePost()}
                                disabled={restrictCommenting || !replyText.replace(/:(\d+):(\d+):/g, 'x').trim()}
                                className="shrink-0 text-[10px] font-bold uppercase text-blue-600 hover:text-blue-800 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                                Post
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ── CommentItem ────────────────────────────────────────────────────────────────

const CommentItem = ({
                         comment, profileUsername, timeAgo, currentUserId, currentUserHabboName, isPageOwner, isAdmin,
                         likedCommentIds, likeCountsByCommentId, onDelete, onBlock, requestConfirm, commentBg,
                         onExternalLink,
                         postId, enableReplies, replyCountsByParentId, repliesByParentId, onReplyPosted,
                         restrictCommenting, restrictionExpiry, restrictLiking, isGuest, isNarrowColumn
                     }) => {
    const emoteCtx = useEmoteContext();
    const [quickReplyText, setQuickReplyText] = React.useState('');
    const replyPostRef = React.useRef(null);

    const navigate = useNavigate();

    const commentHtmlObj = React.useMemo(() => {
        const raw = comment?.content ?? comment?.Content ?? comment?.text ?? comment?.Text ?? "";
        const decoded = stripEmoteSentinels(decodeEntities(raw));
        const mentionHrefForName = (name) => `/members/profile/${encodeURIComponent(String(name || "").trim())}`;
        const html = renderEmotesInHtmlMemo(externalizeAndMentionHtml(decoded, { mentionHrefForName }), emoteCtx);
        return { __html: html };
    }, [comment, emoteCtx]);
    const [isEmotePickerOpen, setIsEmotePickerOpen] = useState(false);
    const emoteInsertRef = useRef(null);

    const openEmotePicker = useCallback((insertFn) => {
        emoteInsertRef.current = insertFn;
        setIsEmotePickerOpen(true);
    }, []);

    const closeEmotePicker = useCallback(() => setIsEmotePickerOpen(false), []);

    const handleEmoteSelected = useCallback((token) => {
        if (typeof emoteInsertRef.current === "function") {
            emoteInsertRef.current(String(token || ""));
        }
        setIsEmotePickerOpen(false);
    }, []);

    const habboName = comment.habboName || comment.HabboName || comment.habbo_name || comment.habbo ||
        comment.authorName || comment.author_name || comment.username || comment.userName || comment.user_name || 'Unknown';
    const commentId = comment.id || comment.ID;
    const commentIdNum = Number(commentId);


    const isOwnComment = String(habboName || '').trim().toLowerCase() === String(currentUserHabboName || '').trim().toLowerCase();
    const canDelete = Boolean(currentUserHabboName) && (isOwnComment || isPageOwner || Boolean(isAdmin));
    const canBlock = Boolean(currentUserHabboName) && !isOwnComment && habboName !== 'Unknown';

    const [likes, setLikes] = React.useState(Number.isFinite(Number(likeCountsByCommentId?.[String(commentId)])) ? Number(likeCountsByCommentId[String(commentId)]) : getLikesCount(comment));
    const [hasLiked, setHasLiked] = React.useState(false);

    React.useEffect(() => {
        const commentIdStr = String(commentId || '');
        const userIdStr = String(currentUserId || '');
        let liked = false;
        if (likedCommentIds?.has(commentIdStr)) { liked = true; }
        else if (comment.Engagements) { liked = comment.Engagements.some(raw => { const e = normalizeEngagement(raw); return String(e.fromUserId || '') === userIdStr && String(e.engagementType || '') === 'like'; }); }
        setHasLiked(liked);
    }, [likedCommentIds, commentId, comment.Engagements, currentUserId]);

    React.useEffect(() => {
        const idStr = String(commentId || '');
        const mapped = likeCountsByCommentId?.[idStr];
        if (Number.isFinite(Number(mapped))) setLikes(Number(mapped));
        else setLikes(getLikesCount(comment));
    }, [commentId, likeCountsByCommentId, comment.likesCount, comment.likes_count, comment.likescount, comment.LikesCount, comment.likes, comment.Likes]);

    const [avatarBg, setAvatarBg] = React.useState(comment.profileBg || '#5A2929');

    React.useEffect(() => {
        if (!comment.profileBg) {
            fetchProfileColor(habboName).then(setAvatarBg);
        }
    }, [habboName, comment.profileBg]);

    const toggleLike = async () => {
        if (restrictLiking) {
            const expiry = restrictionExpiry ? ` until ${new Date(restrictionExpiry).toLocaleString()}` : '';
            toast.warning(`You are restricted from liking${expiry}.`);
            return;
        }

        const prev = hasLiked;
        try {
            if (prev) { setHasLiked(false); setLikes(l => Math.max(0, l - 1)); await deleteProfileEngagement(profileUsername, 'comment', commentId, 'like'); }
            else {
                setHasLiked(true); setLikes(l => l + 1);
                await createProfileEngagement(profileUsername, 'comment', commentId, 'like');
            }
        } catch {
            setHasLiked(prev);
            setLikes(l => prev ? l + 1 : Math.max(0, l - 1));
            toast.error("Failed to update like. Please try again.");
        }
    };

    const goProfile = () => { if (!habboName || habboName === 'Unknown') return; navigate(`/members/profile/${encodeURIComponent(habboName)}`); };

    const confirmDelete = () => requestConfirm({ title: 'Delete comment?', message: 'Delete this comment?', onConfirm: () => onDelete(commentId) });
    const confirmBlock = () =>
        requestConfirm({
            title: "Block user?",
            message: blockConfirmBody(habboName),
            onConfirm: () => onBlock(habboName)
        });

    return (
        <div className={`border-t border-black/10 ${isNarrowColumn ? 'pl-[22px] pr-0' : 'pl-[22px] pr-0 lg2:pl-[52px] lg2:pr-0'}`} style={{ backgroundColor: commentBg }}>
            <div className="flex gap-2 p-2">
                <div className={`${isNarrowColumn ? 'w-[30px] h-[30px]' : 'w-[30px] h-[30px] lg2:w-[48px] lg2:h-[48px]'} relative rounded border border-black/20 overflow-hidden shrink-0 cursor-pointer hover:brightness-95`} style={getAvatarBgStyle(avatarBg)} onClick={goProfile}>
                    <HabboImage habbo={habboName} hotel="COM" size="a" direction={2} head_direction={2} gesture="sml" headonly className="absolute top-5 left-1/2 -translate-x-1/2 -translate-y-1/2 max-w-none" />
                </div>
                <div className="flex-1 min-w-0 flex flex-col">
                    <div className="flex items-center justify-between">
                        <span onClick={goProfile} className="font-bold text-sm text-coal cursor-pointer hover:underline">{habboName}</span>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-coal/50">{timeAgo(comment.createdAt)}</span>
                            {canBlock && (
                                <button onClick={confirmBlock} title="Block user" className="text-coal/30 hover:text-orange-500">
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="9"/><path d="M7.5 7.5l9 9"/></svg>
                                </button>
                            )}
                            {canDelete && (
                                <button onClick={confirmDelete} className="text-coal/40 hover:text-red-500" title="Delete comment">
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12"/></svg>
                                </button>
                            )}
                        </div>
                    </div>
                    <div
                        className="text-sm text-coal/90 mt-0.5 break-words"
                        onClick={(e) => {
                            const a = e.target?.closest?.("a[data-external='1']");
                            if (!a) return;
                            e.preventDefault();
                            const url = a.getAttribute("data-external-url") || "";
                            if (typeof onExternalLink === "function") onExternalLink(url);
                        }}
                        dangerouslySetInnerHTML={commentHtmlObj}
                    />
                    <div className="flex-1 min-h-2" />
                    <div className="border border-black/20 rounded bg-[#ededed]/50 flex items-center overflow-hidden relative">
                        <button
                            onClick={restrictLiking ? undefined : toggleLike}
                            disabled={restrictLiking}
                            className={`font-volterBold text-[9px] px-2 py-2 tracking-wide shrink-0 flex items-center gap-1.5 transition-colors ${
                                restrictLiking
                                    ? 'opacity-40 cursor-not-allowed text-coal/60'
                                    : hasLiked
                                        ? 'text-red-600'
                                        : 'text-coal/60 hover:text-coal'
                            }`}
                        >
                            <img src={hasLiked ? "/img/likeHeart-on.png" : "/img/likeHeart-off.png"} style={{ imageRendering: 'pixelated' }} />
                            <span className="pt-1">
                                {hasLiked ? 'Unlike' : 'Like'} {likes > 0 && `(${likes})`}
                            </span>
                        </button>
                        {enableReplies && !comment.isOptimistic && postId && (
                            <>
                                <div className="self-stretch w-px bg-black/10 shrink-0" />
                                <div className="flex items-center gap-2 px-2 py-1 flex-1 min-w-0">
                                    <PlainEmoteInput
                                        value={quickReplyText}
                                        onChange={setQuickReplyText}
                                        placeholder={isGuest ? "Login to comment." : restrictCommenting ? "You are restricted from commenting." : "Write a reply..."}
                                        emoteCtx={emoteCtx}
                                        maxLength={COMMENT_MAX_CHARS + 20}
                                        disabled={restrictCommenting}
                                        className="text-[11px]"
                                        onSubmit={restrictCommenting ? undefined : () => { replyPostRef.current?.(quickReplyText); setQuickReplyText(""); }}
                                        onOpenEmotePicker={typeof openEmotePicker === "function" ? openEmotePicker : undefined}
                                    />
                                    <button
                                        onClick={restrictCommenting ? undefined : () => { replyPostRef.current?.(quickReplyText); setQuickReplyText(""); }}
                                        disabled={restrictCommenting || !quickReplyText.replace(/:(\d+):(\d+):/g, 'x').trim()}
                                        className="shrink-0 text-[10px] font-bold uppercase text-blue-600 hover:text-blue-800 disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        Post
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
            {enableReplies && !comment.isOptimistic && postId && (
                <ReplyThread
                    parentCommentId={commentId}
                    postId={postId}
                    profileUsername={profileUsername}
                    currentUserId={currentUserId}
                    currentUserHabboName={currentUserHabboName}
                    isPageOwner={isPageOwner}
                    isAdmin={isAdmin}
                    requestConfirm={requestConfirm}
                    commentBg={commentBg}
                    onExternalLink={onExternalLink}
                    onOpenEmotePicker={openEmotePicker}
                    timeAgo={timeAgo}
                    replyCount={Number(replyCountsByParentId?.[String(commentId)] || 0)}
                    replies={repliesByParentId?.[String(commentId)] || []}
                    onReplyPosted={onReplyPosted}
                    restrictCommenting={restrictCommenting}
                    restrictionExpiry={restrictionExpiry}
                    isGuest={isGuest}
                    postRef={replyPostRef}
                />
            )}
            <EmotePickerModal open={isEmotePickerOpen} onClose={closeEmotePicker} onSelect={handleEmoteSelected} emoteCtx={emoteCtx} />
        </div>
    );
};

// ── PostItem ───────────────────────────────────────────────────────────────────

const PostItem = ({
                      post, profileUsername, isOwnProfile, userData, timeAgo, onDeletePost, onBlockUser,
                      likedPostIds, likedCommentIds, likeCountsByPostId, likeCountsByCommentId, requestConfirm, palette,
                      checkCommentRateLimit, recordNewComment, currentUserHabboNameOverride,
                      mentionFriends, onExternalLink, onOpenEmotePicker,
                      restrictPosting, restrictCommenting, restrictLiking, restrictionExpiry, isGuest, previewMode, isNarrowColumn,
                      cloudItemsMap, cloudItemsMapReady,
                      hideThread, footerBar,
                  }) => {
    const emoteCtx = useEmoteContext();

    const navigate = useNavigate();
    const currentUserId = String(userData?.id ?? userData?.ID ?? userData?.userId ?? userData?.userID ?? userData?.UserID ?? userData?.user_id ?? '');
    const currentUserHabboName = currentUserHabboNameOverride || userData?.habboName || userData?.HabboName || userData?.username || userData?.name || '';
    const postId = post.id || post.ID;
    const isAdmin = Boolean(userData?.hasAdminRole) && !previewMode;

    const postAttachments = React.useMemo(() => {
        const raw = post?.content ?? post?.Content ?? post?.text ?? post?.Text ?? "";
        const match = raw.match(/data-images='([^']*)'/);
        if (!match) return [];
        try {
            const refs = JSON.parse(match[1].replace(/&#39;/g, "'"));
            return refs.map(ref => {
                if (ref.id != null) {
                    const cloud = cloudItemsMap?.[ref.id];
                    if (cloud?.deleted) return { ...ref, unavailable: true };
                    if (cloud) return { ...ref, src: cloud.src };
                    if (cloudItemsMapReady) return { ...ref, unavailable: true };
                    return ref.src ? ref : null;
                }
                if (ref.src) return ref;
                return null;
            }).filter(Boolean);
        } catch { return []; }
    }, [post, cloudItemsMap, cloudItemsMapReady]);

    const postHtmlObj = React.useMemo(() => {
        const raw = post?.content ?? post?.Content ?? post?.text ?? post?.Text ?? "";
        const stripped = raw.replace(/<div[^>]*class="fc-attachment-block"[^>]*><\/div>/g, '');
        const decoded = stripEmoteSentinels(decodeEntities(stripped));
        const mentionHrefForName = (name) => `/members/profile/${encodeURIComponent(String(name || "").trim())}`;
        const html = renderEmotesInHtmlMemo(externalizeAndMentionHtml(decoded, { mentionHrefForName }), emoteCtx);
        return { __html: html };
    }, [post, emoteCtx]);

    const [visibleCount, setVisibleCount] = React.useState(0);
    const [replyText, setReplyText] = React.useState("");
    const [postLightboxIndex, setPostLightboxIndex] = React.useState(null);

    const lightboxItems = React.useMemo(() => postAttachments.filter(a => !a.unavailable), [postAttachments]);

    React.useEffect(() => {
        if (postLightboxIndex === null) return;
        if (postLightboxIndex >= lightboxItems.length) setPostLightboxIndex(null);
    }, [lightboxItems, postLightboxIndex]);

    const [hasLiked, setHasLiked] = React.useState(post.isLikedByViewer ?? false);
    const [likes, setLikes] = React.useState(post.likesCount ?? getLikesCount(post));
    const [posterAvatarBg, setPosterAvatarBg] = React.useState(post.profileBg || '#5A2929');

    const [allComments, setAllComments] = React.useState(() => {
        if (post.comments) return post.comments.map(c => normalizeComment(c));
        return [];
    });

    const [justPostedComment, setJustPostedComment] = React.useState(null);
    const [mentionOpen, setMentionOpen] = React.useState(false);
    const [mentionItems, setMentionItems] = React.useState([]);
    const [mentionAnchorRect, setMentionAnchorRect] = React.useState(null);
    const [mentionQuery, setMentionQuery] = React.useState("");

    const COMMENT_PAGE_SIZE = 20;

    const [commentsOffset, setCommentsOffset] = React.useState(0);
    const [commentsHasMore, setCommentsHasMore] = React.useState(false);
    const [commentsTotal, setCommentsTotal] = React.useState(0);
    const [commentsLoadingMore, setCommentsLoadingMore] = React.useState(false);

    const parentComments = React.useMemo(
        () => allComments.filter(c => c.parentCommentID == null),
        [allComments]
    );

    const repliesByParentId = React.useMemo(() => {
        const map = {};
        for (const c of allComments) {
            if (c?.parentCommentID == null) continue;
            const k = String(c.parentCommentID);
            (map[k] ||= []).push(c);
        }
        for (const k of Object.keys(map)) {
            map[k] = [...map[k]].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        }
        return map;
    }, [allComments]);

    const replyCountsByParentId = React.useMemo(() => {
        const out = {};
        for (const [k, arr] of Object.entries(repliesByParentId)) out[k] = arr.length;
        return out;
    }, [repliesByParentId]);

    React.useEffect(() => {
        if (!postId || post?.isOptimistic || hideThread) return;
        fetchComments("reset");
    }, [postId, hideThread]);

    React.useEffect(() => {
        if (!postId || post?.isOptimistic) return;
        fetchProfileColor(profileUsername).then(setPosterAvatarBg);
    }, [postId, profileUsername]);

    const fetchComments = async (mode = "reset") => {
        const nextOffset = mode === "append" ? commentsOffset + COMMENT_PAGE_SIZE : 0;

        try {
            if (mode === "append") setCommentsLoadingMore(true);

            const res = await listPostComments(postId, COMMENT_PAGE_SIZE, nextOffset);

            const incoming = Array.isArray(res?.comments) ? res.comments.map(c => normalizeComment(c)) : [];
            const total = Number(res?.total || 0);
            const hasMore = Boolean(res?.hasMore);

            setCommentsTotal(total);
            setCommentsHasMore(hasMore);
            setCommentsOffset(nextOffset);

            if (mode === "append") {
                setAllComments(prev => {
                    const existing = new Set((prev || []).map(x => String(x?.id || x?.ID || "")));
                    const merged = Array.isArray(prev) ? [...prev] : [];
                    for (const c of incoming) {
                        const id = String(c?.id || c?.ID || "");
                        if (id && !existing.has(id)) merged.push(c);
                    }
                    return merged;
                });
            } else {
                setAllComments(incoming);
                setJustPostedComment(null);
            }
        } catch {
            toast.error("Failed to load comments.");
        } finally {
            if (mode === "append") setCommentsLoadingMore(false);
        }
    };

    const handleLike = async () => {
        if (restrictLiking) {
            const expiry = restrictionExpiry ? ` until ${new Date(restrictionExpiry).toLocaleString()}` : '';
            toast.warning(`You are restricted from liking${expiry}.`);
            return;
        }
        const prior = hasLiked;
        const priorLikes = likes;

        setHasLiked(!prior);
        setLikes(l => prior ? Math.max(0, l - 1) : l + 1);

        try {
            if (prior) {
                await deleteProfileEngagement(profileUsername, 'post', postId, 'like');
            } else {
                await createProfileEngagement(profileUsername, 'post', postId, 'like');
            }
        } catch {
            setHasLiked(prior);
            setLikes(priorLikes);
            toast.error("Failed to update like.");
        }
    };

    const handleReplyPosted = (evt) => {
        if (!evt || evt.kind !== "optimistic-reply") return;
        const reply = evt.reply;
        if (!reply) return;

        setAllComments((prev) => {
            const next = Array.isArray(prev) ? [...prev] : [];
            const rid = String(reply.id || reply.ID || "");
            if (rid && next.some((c) => String(c.id || c.ID || "") === rid)) return next;
            next.push(reply);
            return next;
        });
    };

    const handleComment = async () => {
        const text = stripEmoteSentinels(String(replyText ?? "")).trim();
        if (!text) return;
        if (text.length > COMMENT_MAX_CHARS) { toast.warning(`Comment too long.`); return; }

        if (restrictCommenting) {
            const expiry = restrictionExpiry ? ` until ${new Date(restrictionExpiry).toLocaleString()}` : '';
            toast.warning(`You are restricted from commenting${expiry}.`);
            return;
        }

        if (typeof checkCommentRateLimit === "function") {
            const rl = checkCommentRateLimit(currentUserHabboName);
            if (!rl.allowed) { toast.warning(rl.reason); return; }
        }

        const now = new Date().toISOString();
        const encoded = encodeEmojis(text);

        const optimistic = normalizeComment({
            id: `optimistic-${Date.now()}`,
            content: encoded,
            createdAt: now,
            likesCount: 0,
            habboName: currentUserHabboName,
            isOptimistic: true
        });

        setJustPostedComment(optimistic);
        setReplyText("");
        if (typeof recordNewComment === "function") recordNewComment(currentUserHabboName, now);

        try {
            const res = await createPostComment(postId, encoded);
            if (res?.comment) {
                const confirmed = normalizeComment({ ...res.comment, isOptimistic: false });
                setAllComments(prev => {
                    const next = (Array.isArray(prev) ? prev : []).filter(
                        c => String(c.id || c.ID || '') !== String(optimistic.id)
                    );
                    return [...next, confirmed];
                });
                setJustPostedComment(null);
                setCommentsTotal(t => t + 1);
            } else {
                await fetchComments("reset");
            }
        } catch {
            setJustPostedComment(null);
            toast.error("Failed to post comment.");
        }
    };

    const handleDeleteComment = async (commentId) => {
        try {
            await hideProfileComment(commentId);
            const idStr = String(commentId);
            setAllComments(prev => prev.filter(c => {
                const cid = String(c.id || c.ID);
                const pid = c.parentCommentID != null ? String(c.parentCommentID) : null;
                return cid !== idStr && pid !== idStr;
            }));
            if (justPostedComment?.id === commentId) setJustPostedComment(null);
        } catch { toast.error("Failed to delete comment."); }
    };

    const handleBlockFromComment = async (habboName) => {
        try {
            await onBlockUser(habboName);
            const target = String(habboName).toLowerCase();
            setAllComments(prev => prev.filter(c => {
                const name = String(c.habboName || c.HabboName || '').toLowerCase();
                return name !== target;
            }));
        } catch { toast.error("Failed to block user."); }
    };

    const getCommentId = (c) => String(c?.id ?? c?.ID ?? "");

    const getReplyCount = (c) => {
        const id = getCommentId(c);
        const n = Number(replyCountsByParentId?.[id] ?? 0);
        return Number.isFinite(n) ? n : 0;
    };

    const getLikeCount = (c) => {
        const id = getCommentId(c);
        const mapped = likeCountsByCommentId?.[id];
        const n = Number.isFinite(Number(mapped)) ? Number(mapped) : getLikesCount(c);
        return Number.isFinite(n) ? n : 0;
    };

    const getTime = (c) => {
        const t = new Date(c?.createdAt).getTime();
        return Number.isFinite(t) ? t : 0;
    };

    const featured = parentComments.length
        ? [...parentComments].sort((a, b) => {
            const dr = getReplyCount(b) - getReplyCount(a);
            if (dr !== 0) return dr;
            const dl = getLikeCount(b) - getLikeCount(a);
            if (dl !== 0) return dl;
            return getTime(b) - getTime(a);
        })[0]
        : null;

    const rest = parentComments
        .filter((c) => getCommentId(c) !== getCommentId(featured))
        .sort((a, b) => {
            const dl = getLikeCount(b) - getLikeCount(a);
            if (dl !== 0) return dl;
            return getTime(b) - getTime(a);
        });

    const displayed = [featured, ...rest.slice(0, visibleCount)].filter(Boolean);
    const goProfile = () => navigate(`/members/profile/${encodeURIComponent(profileUsername)}`);

    const commentProps = {
        profileUsername,
        timeAgo,
        currentUserId,
        currentUserHabboName,
        isPageOwner: isOwnProfile,
        isAdmin,
        likedCommentIds,
        likeCountsByCommentId,
        requestConfirm,
        commentBg: palette?.commentBg || "#ffffff",
        isNarrowColumn,
        onExternalLink,
        postId,
        enableReplies: true,
        replyCountsByParentId,
        repliesByParentId,
        onReplyPosted: async () => {
            await fetchComments("reset");
        },
        onDelete: (id) => requestConfirm({
            title: "Delete comment?",
            message: "Delete this comment?",
            onConfirm: () => handleDeleteComment(id)
        }),
        onBlock: (name) => requestConfirm({
            title: "Block user?",
            message: `Block ${name}?`,
            onConfirm: () => handleBlockFromComment(name)
        }),
        restrictCommenting,
        restrictionExpiry,
        restrictLiking,
    };

    const totalTopLevel = parentComments.length;
    const remainingTopLevel = Math.max(0, totalTopLevel - displayed.length);

    React.useEffect(() => {
        const idStr = String(postId || '');
        if (likedPostIds?.has(idStr)) {
            setHasLiked(true);
        }
    }, [likedPostIds, postId]);

    return (
        <div className="border-b border-b-[10px] border-black/20 flex flex-col" style={{ backgroundColor: palette?.postBg || '#ffffff' }}>
        {/* Main Post UI */}
            <div className="p-3 flex gap-3">
                <div
                    className={`${isNarrowColumn ? 'w-[48px] h-[48px]' : 'w-[48px] h-[48px] lg2:w-[96px] lg2:h-[96px]'} border-2 rounded border-black/10 shrink-0 overflow-hidden flex items-center justify-center cursor-pointer hover:brightness-95`}
                    style={{ ...getAvatarBgStyle(posterAvatarBg) }}
                    onClick={goProfile}
                >
                    <HabboImage habbo={profileUsername} hotel="COM" size="a" direction={2} head_direction={2} gesture="sml" headonly="true" />
                </div>
                <div className="flex-1 min-w-0 flex flex-col">
                    <div className="flex justify-between items-center">
                        <span
                            className="font-bold text-coal cursor-pointer hover:underline"
                            onClick={goProfile}
                        >
                            {profileUsername}
                        </span>
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] text-coal/40 uppercase font-bold tracking-wider">{timeAgo(post.createdAt)}</span>
                            {(isOwnProfile || isAdmin) && (
                                <button onClick={() => requestConfirm({ title: 'Delete post?', message: 'Delete this post?', onConfirm: () => onDeletePost(postId) })} className="text-coal/30 hover:text-red-500">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                                </button>
                            )}
                        </div>
                    </div>
                    <div
                        className="text-coal text-sm break-words leading-tight pb-1"
                        onClick={(e) => {
                            const a = e.target?.closest?.("a[data-external='1']");
                            if (!a) return;
                            e.preventDefault();
                            const url = a.getAttribute("data-external-url") || "";
                            if (typeof onExternalLink === "function") onExternalLink(url);
                        }}
                        dangerouslySetInnerHTML={postHtmlObj}
                    />
                    {postAttachments.length > 0 && (
                        <div className={`mt-1 mb-1 ${postAttachments.length > 1 ? 'grid grid-cols-2 gap-1' : ''}`}>
                            {postAttachments.map((img, idx) => {
                                const isOddLast = postAttachments.length % 2 !== 0 && idx === postAttachments.length - 1;
                                return (
                                    <div
                                        key={img.id}
                                        className={`rounded border border-black/20 bg-[#ededed]/50 overflow-hidden ${postAttachments.length === 1 ? 'h-48' : 'h-32'} ${isOddLast ? 'col-span-2' : ''} ${img.unavailable ? '' : 'cursor-pointer'}`}
                                        onClick={img.unavailable ? undefined : () => setPostLightboxIndex(lightboxItems.indexOf(img))}
                                    >
                                        {img.unavailable ? (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <p className="text-xs text-coal/40 text-center px-3">This image is unavailable.</p>
                                            </div>
                                        ) : (
                                            <img
                                                src={img.src}
                                                alt={img.name || ''}
                                                className="w-full h-full object-cover"
                                                draggable={false}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {postLightboxIndex !== null && (
                        <ImageLightbox
                            items={lightboxItems}
                            index={postLightboxIndex}
                            onClose={() => setPostLightboxIndex(null)}
                            onChange={setPostLightboxIndex}
                        />
                    )}
                    <div className="flex-1 min-h-2" />
                    <div className="border border-black/20 rounded bg-[#ededed]/50 flex items-center overflow-hidden relative">
                        <button
                            onClick={restrictLiking ? undefined : handleLike}
                            disabled={restrictLiking}
                            className={`font-volterBold text-[9px] px-2 py-2 shrink-0 flex items-center gap-1.5 transition-colors ${
                                restrictLiking
                                    ? 'opacity-40 cursor-not-allowed text-coal/60'
                                    : hasLiked
                                        ? 'text-red-600'
                                        : 'text-coal/60 hover:text-coal'
                            }`}
                        >
                            <img src={hasLiked ? "/img/likeHeart-on.png" : "/img/likeHeart-off.png"} style={{ imageRendering: 'pixelated' }} />
                            <span className="pt-1">
                                {hasLiked ? 'Unlike' : 'Like'} {likes > 0 && `(${likes})`}
                            </span>
                        </button>
                        {hideThread ? (
                            typeof footerBar === 'function' ? footerBar() : null
                        ) : userData && (
                            <>
                                <div className="self-stretch w-px bg-black/10 shrink-0" />
                                <div className="flex items-center gap-2 px-2 py-1 flex-1 min-w-0">
                                    <PlainEmoteInput
                                        value={replyText}
                                        onChange={setReplyText}
                                        className="text-xs"
                                        placeholder={isGuest ? "Login to comment." : restrictCommenting ? "You are restricted from commenting." : "Reply..."}
                                        emoteCtx={emoteCtx}
                                        maxLength={COMMENT_MAX_CHARS + 20}
                                        disabled={restrictCommenting}
                                        onSubmit={restrictCommenting ? undefined : handleComment}
                                        onOpenEmotePicker={typeof onOpenEmotePicker === "function" ? onOpenEmotePicker : undefined}
                                    />
                                    <button
                                        onClick={restrictCommenting ? undefined : handleComment}
                                        disabled={restrictCommenting || !replyText.replace(/:(\d+):(\d+):/g, 'x').trim()}
                                        className="shrink-0 pt-1 font-volterBold text-[9px] uppercase text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        Post
                                    </button>
                                </div>
                                <MentionSuggestions
                                    open={mentionOpen}
                                    items={mentionItems}
                                    anchorRect={mentionAnchorRect}
                                    onClose={() => setMentionOpen(false)}
                                    onPick={(f) => {
                                        const name = f.habboName;
                                        const v = replyText;
                                        const next = v.replace(/@\w+$/, `@${name} `);
                                        setReplyText(next);
                                        setMentionOpen(false);
                                    }}
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Comments Area */}
            {!hideThread && (
                <div className="border-t border-black/5" style={{ backgroundColor: palette?.commentsAreaBg || '#ffffff' }}>
                    {displayed.map(c => (
                        <CommentItem key={c.id || c.ID} comment={c} {...commentProps} onDelete={handleDeleteComment} onBlock={handleBlockFromComment} />
                    ))}
                    {justPostedComment && (
                        <CommentItem key={justPostedComment.id} comment={justPostedComment} {...commentProps} onDelete={handleDeleteComment} onBlock={handleBlockFromComment} />
                    )}
                    {remainingTopLevel > 0 && (
                        <button
                            onClick={async () => {
                                setVisibleCount(v => v + 5);
                                if (commentsHasMore && !commentsLoadingMore) await fetchComments("append");
                            }}
                            className="w-full p-2 text-[10px] font-bold text-coal/50 hover:bg-black/5 text-left uppercase"
                        >
                            {`View more comment(s) - [${remainingTopLevel} more...]`}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default PostItem;
